import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import HeroSection from "../Story-Elements/HeroSection";
import NavigationBox from "../Story-Elements/NavigationBox";
import ArticleBody from "./components/ArticleBody";
import AlsoReadSection from "../Story-Elements/AlsoReadSection";
import { MembershipGateBanner } from "../../atoms/membership-gate-banner";
import { isStoryVisibleToUser } from "../../../utils/forum-filter";
import { NotFoundPage } from "../../pages/not-found/index";
import AdCard from "../Story-Elements/AdCard";
import LoadingIndicatorComponent from "../../atoms/loading-indicator";
import { getStoryData } from "./stories";
import { getAdCards } from "../../utils";
import "./text-story.m.css";


const TextStory = ({
  story,
  config,
  adWidget,
  adPlaceholder,
  hasAccess,
}) => {
  // Get member and subscriber metadata from Redux store
  const member = useSelector((state) => get(state, ["member"], null));
  const subscriberMetadata = useSelector((state) => get(state, ["subscriberMetadata"], null));
  const isAuthInitializing = useSelector((state) => get(state, ["isAuthInitializing"], false));

  // Loading states - start with loading for forum-based stories
  const [isAccessCheckComplete, setIsAccessCheckComplete] = useState(false);
  const [userHasAccess, setUserHasAccess] = useState(true);
  const [participantCheckComplete, setParticipantCheckComplete] = useState(false);
  const [isParticipantAllowed, setIsParticipantAllowed] = useState(true);

  const {
    linkedEntity,
    desktopSvg: initialDesktopSvg,
    entityName,
    sectionName,
    sectionSlug,
    firstPublishedAt,
    quarter,
    timezone,
  } = getStoryData(story);

  const [desktopSvg, setDesktopSvg] = useState(initialDesktopSvg);

  useEffect(() => {
    if (linkedEntity?.id) {
      fetch(`/api/v1/entities/${linkedEntity.id}`)
        .then(res => res.json())
        .then(data => {
          const url = data?.entity?.["desktop-svg"]?.[0]?.url;
          if (url) setDesktopSvg(url);
        })
        .catch(err => console.error("Error fetching entity:", err));
    }
  }, [linkedEntity?.id]);

  // Get forum attribute from story metadata
  const forumAttribute = get(story, ['metadata', 'story-attributes', 'forum'], []);
  const eventParticipantsObjects = get(story, ['metadata', 'story-attributes', 'eventparticipants'], []);
  const forumAttributes = Array.isArray(forumAttribute) ? forumAttribute : (forumAttribute ? [forumAttribute] : []);

  // Check if this is a forum-based story that needs access control
  const isForumBasedStory = forumAttributes.length > 0;

  // Check if this story has event-participants restrictions
  const hasEventParticipantsRestriction = eventParticipantsObjects.length > 0;

  // Check if subscriberMetadata is still loading
  // If user is logged in but subscriberMetadata is null, it's still loading
  const isMetadataLoading = member && subscriberMetadata === null;

  // Effect to handle event-participants access check
  useEffect(() => {
    if (isAuthInitializing) {
      return;
    }
    if (hasEventParticipantsRestriction) {
      if (!member?.email) {
        // User is not logged in, redirect to 404
        setIsParticipantAllowed(false);
        setParticipantCheckComplete(true);
        return;
      }

      setParticipantCheckComplete(false);
      setIsParticipantAllowed(false);

      const checkParticipantAccess = async () => {
        try {
          let userIsParticipant = false;

          // Check each event-participants object
          for (const eventParticipantsObj of eventParticipantsObjects) {
            const participants = eventParticipantsObj.participants || [];

            // Check if any participant's name matches the user's email
            const matchingParticipant = participants.find(participant =>
              participant.name === member.email
            );

            if (matchingParticipant) {
              userIsParticipant = true;
              break;
            }
          }

          setIsParticipantAllowed(userIsParticipant);
          setParticipantCheckComplete(true);
        } catch (error) {
          console.error('Error checking participant access:', error);
          setIsParticipantAllowed(false);
          setParticipantCheckComplete(true);
        }
      };

      checkParticipantAccess();
    } else {
      // No event-participants restriction
      setParticipantCheckComplete(true);
      setIsParticipantAllowed(true);
    }
  }, [hasEventParticipantsRestriction, eventParticipantsObjects, member?.email, isAuthInitializing]);

  // Effect to handle access check when member/metadata changes
  useEffect(() => {
    if (isForumBasedStory) {
      // Always start with loading state
      setIsAccessCheckComplete(false);
      setUserHasAccess(false);

      // Don't check access if auth is initializing or metadata is still loading
      if (isAuthInitializing || isMetadataLoading) {
        return;
      }

      const checkAccess = () => {
        // Use isStoryVisibleToUser to determine access based on member state and subscriberMetadata
        const hasStoryAccess = isStoryVisibleToUser(member, subscriberMetadata, story);
        setUserHasAccess(hasStoryAccess);
        setIsAccessCheckComplete(true);
      };

      checkAccess();
    } else {
      // For non-forum stories, set access immediately
      setIsAccessCheckComplete(true);
      setUserHasAccess(true);
    }
  }, [member, subscriberMetadata, story, isForumBasedStory, isMetadataLoading, isAuthInitializing]);

  // Check if story requires membership and user doesn't have access
  const showMembershipGate = isAccessCheckComplete && !userHasAccess && isForumBasedStory;

  // Hide back to top button when membership gate is shown
  useEffect(() => {
    const backToTopBtn = document.getElementById('backToTopBtn');
    if (showMembershipGate && backToTopBtn) {
      backToTopBtn.style.display = 'none';
    }

    // Show back to top button when membership gate is hidden
    return () => {
      if (backToTopBtn) {
        backToTopBtn.style.display = '';
      }
    };
  }, [showMembershipGate]);


  // Handle participant access control
  if (hasEventParticipantsRestriction) {
    if (!participantCheckComplete) {
      return null; // Still checking, don't render anything
    }
    if (!isParticipantAllowed) {
      return <NotFoundPage />; // User is not allowed, show 404
    }
  }

  // Handle forum-based access check loading state
  if (isForumBasedStory && (isAuthInitializing || isMetadataLoading || !isAccessCheckComplete)) {
    return <LoadingIndicatorComponent />; // Still checking access, don't display anything to prevent flickering
  }

  // Determine the forum type for the banner
  const getForumType = () => {
    if (forumAttributes.length > 0 && !forumAttributes.includes('All-forums') && !forumAttributes.includes('All-Forums')) {
      return forumAttributes.join(', '); // Join all forum attributes with comma
    }
  };

  const adCards = getAdCards(story, get);
  const storyTemplate = get(story, ["story-template"], "");
  const isLeadershipFocus = storyTemplate === "leadership-in-focus";
  const isVideoStory = storyTemplate === "video";

  const shouldRenderBody = !isForumBasedStory || (isAccessCheckComplete && userHasAccess);

  const shouldShowHeadlineOnly = isForumBasedStory && isAccessCheckComplete && !userHasAccess;

  return (
    <div styleName={`text-story ${isLeadershipFocus ? "leadership-in-focus-bg" : ""}`} className="article-print-wrapper article-printable">
      <div styleName="hero-nav-container">
        <HeroSection
          story={story}
          desktopSvg={desktopSvg}
          sectionName={sectionName}
          sectionSlug={sectionSlug}
          entityName={entityName}
          quarter={quarter}
          firstPublishedAt={firstPublishedAt}
          config={config}
          shouldShowHeadlineOnly={shouldShowHeadlineOnly}
          hideListenPrint={isVideoStory}
        />

        <NavigationBox story={story} />
      </div>

      <div styleName="content-wrapper">
        <div styleName="article-container" className="article-container">
          {shouldRenderBody && (
            <ArticleBody story={story} hasAccess={hasAccess} timezone={timezone} isLeadershipFocus={isLeadershipFocus} />
          )}
        </div>

        {adCards.length > 0 && (
          <div styleName="desktop-ad-container">
            {adCards.map((adCard, adIndex) => (
              <AdCard
                key={`desktop-ad-${adIndex}`}
                card={adCard}
                isDesktop={true}
              />
            ))}
          </div>
        )}
      </div>

      <AlsoReadSection storyId={story.id} isLeadershipFocus={isLeadershipFocus} />

      {/* Show membership gate banner when user doesn't have access */}
      {isForumBasedStory && showMembershipGate && (
        <MembershipGateBanner
          isVisible={true}
          reportTitle={story.headline || "This report"}
          forumType={getForumType()}
          exploreLink="/collection/research-hub"
          isLoggedIn={!!member}
          userCurrentForum={get(subscriberMetadata, 'primary-role') || get(subscriberMetadata, 'secondary-role')}
        />
      )}
    </div>
  );
};

TextStory.propTypes = {
  story: PropTypes.object.isRequired,
  config: PropTypes.object,
  adWidget: PropTypes.func,
  adPlaceholder: PropTypes.object,
  hasAccess: PropTypes.bool,
};

export default TextStory;